import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConfigInterface, LineItem, SalesOrdersService } from '@vendasta/sales-orders';
import { UntypedFormGroup } from '@angular/forms';
import { CreateOrderDetailsComponent } from '../../create-order-details/create-order-details.component';
import { AccountGroup } from '@vendasta/account-group';
import { FileInfo } from '@vendasta/galaxy/uploader';
import { SalesOrderConfirmationComponent } from '@vendasta/sales-ui';
import { NotesComponent } from '../../notes/notes.component';
import { AdminAttachmentsComponent } from '../../attachments/admin-attachments/admin-attachments.component';
import { EnvironmentService, Environment } from '@galaxy/core';
import { TagsComponent } from '../../tags/tags.component';
import {
  AppVariablePriceMap,
  PackageVariablePriceMap,
  VariablePriceInputs,
} from '../../variable-prices/variable-prices.component';
import { NewOrderDetails } from '../../tags/tags.interface';
import { UILineItemWithEditionPricing } from '../../../shared/interface';
import { App, AppKey, AppPrices, FieldMask } from '@vendasta/marketplace-apps';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { AppPartnerService, AppPricingService } from '@galaxy/marketplace-apps';
import { BillingService } from '@galaxy/billing';
import { HttpErrorResponse } from '@angular/common/http';
import { Package, PackageService } from '@vendasta/marketplace-packages';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  selector: 'orders-create-order',
  templateUrl: './create-order.component.html',
  styleUrls: ['./create-order.component.scss'],
  standalone: false,
})
export class CreateOrderComponent implements OnInit, OnDestroy {
  @Input() business: AccountGroup;
  @Input() formGroup: UntypedFormGroup;
  @Input() selectedLineItems: LineItem[];

  @ViewChild('createOrderDetailsComponent') createOrderDetailsComponent: CreateOrderDetailsComponent;
  @ViewChild('salesOrderConfirmation') salesOrderConfirmation: SalesOrderConfirmationComponent;
  @ViewChild('adminNotes') adminNotes: NotesComponent;
  @ViewChild('customerNotes') customerNotes: NotesComponent;
  @ViewChild('adminAttachmentsComponent') adminAttachmentsComponent: AdminAttachmentsComponent;
  @ViewChild('tagsComponent') tagsComponent: TagsComponent;

  orderDetailsFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  attachmentsUploadUrl = 'https://sales-orders-prod.apigateway.co/file/upload';

  files: FileInfo[] = [];
  newOrderDetails: NewOrderDetails;
  currencyOverride: string = null;

  orderConfig$: Observable<ConfigInterface>;
  variablePriceInputs$: Observable<VariablePriceInputs[]>;
  partnerCurrency$: Observable<string>;
  variablePricesMap$$ = new BehaviorSubject<AppVariablePriceMap>(new Map());
  variablePackagePricesMap$$ = new BehaviorSubject<PackageVariablePriceMap>(new Map());
  private destroyed$$ = new Subject<void>();

  constructor(
    private snackbarService: SnackbarService,
    private environmentService: EnvironmentService,
    private appSdk: AppPartnerService,
    private appPricingService: AppPricingService,
    private billingService: BillingService,
    private salesOrdersService: SalesOrdersService,
    private readonly packageSdk: PackageService,
  ) {}

  ngOnInit(): void {
    if (this.environmentService.getEnvironment() === Environment.DEMO) {
      this.attachmentsUploadUrl = 'https://sales-orders-demo.apigateway.co/file/upload';
    }
    this.newOrderDetails = {
      partnerId: this.business.accountGroupExternalIdentifiers.partnerId,
      marketId: this.business.accountGroupExternalIdentifiers.marketId,
    };

    this.orderConfig$ = this.salesOrdersService
      .getConfig(this.business.accountGroupExternalIdentifiers.partnerId, this.business.accountGroupId)
      .pipe(shareReplay(1));

    this.partnerCurrency$ = this.billingService
      .getMerchantWholesaleCurrency(this.business.accountGroupExternalIdentifiers.partnerId)
      .pipe(shareReplay(1));
  }

  getCreateOrderDetailsComponent(): CreateOrderDetailsComponent {
    return this.createOrderDetailsComponent;
  }

  getSalesOrderConfirmationComponent(): SalesOrderConfirmationComponent {
    return this.salesOrderConfirmation;
  }

  getAdminNotesComponent(): NotesComponent {
    return this.adminNotes;
  }

  getCustomerNotesComponent(): NotesComponent {
    return this.customerNotes;
  }

  getAdminAttachmentsComponent(): AdminAttachmentsComponent {
    return this.adminAttachmentsComponent;
  }

  getTagsComponent(): TagsComponent {
    return this.tagsComponent;
  }

  appendLineItemsToCorrespondiongProductIDList(
    currentLineItems: UILineItemWithEditionPricing[],
    appIDs: string[],
    packageIDs: string[],
  ): void {
    currentLineItems.map((currentLineItem) => {
      if (currentLineItem.lineItem?.appKey?.appId) {
        appIDs.push(currentLineItem.lineItem.appKey.appId);
      } else if (currentLineItem.lineItem?.packageId) {
        packageIDs.push(currentLineItem.lineItem?.packageId);
      }
    });
  }

  updateAppPrices(currentLineItems: UILineItemWithEditionPricing[]): void {
    const appIDs: string[] = [];
    const packageIDs: string[] = [];
    let storedPackages: Package[] = [];
    let storedApps: App[] = [];

    this.appendLineItemsToCorrespondiongProductIDList(currentLineItems, appIDs, packageIDs);

    //Observable to get apps from packages given list of packageIDs and appIDs computed above
    const appsFromPackages$ = (
      packageIDs.length < 1 ? (of([]) as Observable<Package[]>) : this.packageSdk.getMulti(packageIDs)
    ).pipe(
      map((packages) => {
        storedPackages = packages;
        return this.getAppIDsFromPackage(packages);
      }),
      switchMap((appIDsFromPackages: string[]) => {
        const appKeys: AppKey[] = this.convertAppIDsIntoAppKeys(appIDsFromPackages);
        return appKeys.length < 1
          ? (of([]) as Observable<App[]>)
          : this.appSdk.getMulti(appKeys, this.newOrderDetails.partnerId, this.newOrderDetails.marketId, null, true);
      }),
    );

    //This pipe attempts to add apps taken from packages to the appIDs list, which is then used in the GetMultiPricing call
    const pricesForAllApps$ = appsFromPackages$.pipe(
      switchMap((appsFromPackages) => {
        appsFromPackages.map((currentPackageApp) => {
          appIDs.push(currentPackageApp.key.appId);
        });

        storedApps = appsFromPackages;

        if (appIDs.length > 0) {
          return this.appPricingService.getMultiPricing(
            appIDs,
            new FieldMask({ paths: ['wholesale'] }),
            this.business.accountGroupExternalIdentifiers.partnerId,
            null,
          );
        } else {
          return of([]);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this.snackbarService.openErrorSnack('Error getting prices for order: ' + err);
        console.error(err);
        return of(null);
      }),
    );

    //This pipe determines whether a given app price should get external data (icon, name) from the line item, or from the app and package models
    this.variablePriceInputs$ = pricesForAllApps$.pipe(
      map((appPrices) => {
        return appPrices
          .filter((appPrice) => appPrice.pricesForContexts['wholesale'].billingConfiguration?.usesVariablePricing)
          .map((appPrice) => {
            const index = this.getIndexOfLineItem(currentLineItems, appPrice);
            if (index > -1) {
              return {
                appPrice: appPrice.pricesForContexts['wholesale'],
                name: currentLineItems[index].name,
                iconUrl: currentLineItems[index]?.iconUrl,
              } as VariablePriceInputs;
            } else {
              //App not found in line items; app must be part of a package
              const appModelData = this.findAppMatchingAppPrice(storedApps, appPrice);

              const packageModel = this.findPackageMatchingAppPrice(storedPackages, appPrice);

              return {
                appPrice: appPrice.pricesForContexts['wholesale'],
                name: appModelData.sharedMarketingInformation.name,
                iconUrl: appModelData.sharedMarketingInformation.iconUrl,
                packageID: packageModel.packageId,
                packageName: packageModel.name,
                packageIconUrl: packageModel.icon,
              } as VariablePriceInputs;
            }
          });
      }),
      shareReplay(1),
    );

    this.variablePriceInputs$.pipe(takeUntil(this.destroyed$$)).subscribe((variablePrices) => {
      const variablePricesMap = this.variablePricesMap$$.getValue();
      const variablePackagePricesMap = this.variablePackagePricesMap$$.getValue();

      // Store itemIDs of variablePriceInputs
      const variablePriceInputIds = new Set<string>();

      variablePrices.forEach((variablePrice: VariablePriceInputs) => {
        if (variablePrice.packageID) {
          variablePriceInputIds.add(variablePrice.packageID);
          if (!variablePackagePricesMap.has(variablePrice.packageID)) {
            variablePackagePricesMap.set(variablePrice.packageID, new Map());
          }
          if (!variablePackagePricesMap.get(variablePrice.packageID).has(variablePrice.appPrice?.appId)) {
            variablePackagePricesMap.get(variablePrice.packageID).set(variablePrice.appPrice?.appId, variablePrice);
          }
        } else {
          variablePriceInputIds.add(variablePrice.appPrice?.appId);
          if (!variablePricesMap.has(variablePrice.appPrice?.appId)) {
            variablePricesMap.set(variablePrice.appPrice?.appId, variablePrice);
          }
        }
      });
      // Handle line items that were removed from the order by deleting keys of items
      // that are no longer in the variablePriceInputs array.
      variablePricesMap.forEach((_, key) => {
        if (!variablePriceInputIds.has(key)) {
          variablePricesMap.delete(key);
        }
      });
      variablePackagePricesMap.forEach((_, key) => {
        if (!variablePriceInputIds.has(key)) {
          variablePackagePricesMap.delete(key);
        }
      });

      this.variablePricesMap$$.next(new Map(variablePricesMap));
      this.variablePackagePricesMap$$.next(new Map(variablePackagePricesMap));
    });
  }

  updateMap(event: { appId: string; customPrice: number; packageId?: string }, partnerCurrency: string) {
    if (event.packageId) {
      const variablePricesPackageMap = this.variablePackagePricesMap$$.getValue();
      const variablePrice = variablePricesPackageMap.get(event.packageId).get(event.appId);
      variablePrice.appPrice.pricesForEditions[''].pricesForCurrencies[
        partnerCurrency
      ].pricesForFrequencies[0].pricingRules[0].price = event.customPrice;
      variablePricesPackageMap.get(event.packageId).set(event.appId, variablePrice);
      this.variablePackagePricesMap$$.next(new Map(variablePricesPackageMap));
    } else {
      const variablePricesMap = this.variablePricesMap$$.getValue();
      const variablePrice = variablePricesMap.get(event.appId);
      variablePrice.appPrice.pricesForEditions[''].pricesForCurrencies[
        partnerCurrency
      ].pricesForFrequencies[0].pricingRules[0].price = event.customPrice;
      variablePricesMap.set(event.appId, variablePrice);
      this.variablePricesMap$$.next(new Map(variablePricesMap));
    }
  }

  getVariableMap(): AppVariablePriceMap {
    return this.variablePricesMap$$.getValue();
  }

  getPackageVariableMap(): PackageVariablePriceMap {
    return this.variablePackagePricesMap$$.getValue();
  }

  getAppIDsFromPackage(packages: Package[]): string[] {
    const appIDsFromPackages: string[] = [];
    packages.forEach((currentPackage) => {
      currentPackage.lineItems.lineItems.forEach((currentPackageLineItem) => {
        appIDsFromPackages.push(currentPackageLineItem.id);
      });
    });
    return appIDsFromPackages;
  }

  convertAppIDsIntoAppKeys(appIDsFromPackages: string[]): AppKey[] {
    return appIDsFromPackages.map((currentAppID) => {
      return {
        appId: currentAppID,
      } as AppKey;
    });
  }

  getIndexOfLineItem(currentLineItems: UILineItemWithEditionPricing[], appPrice: AppPrices): number {
    return currentLineItems.findIndex((currentLineItem) => {
      if (currentLineItem.lineItem?.appKey?.appId) {
        return currentLineItem.lineItem.appKey.appId === appPrice.appId;
      }
    });
  }

  findAppMatchingAppPrice(appsFromPackages: App[], appPrice: AppPrices): App {
    return appsFromPackages.find((currentApp) => {
      return currentApp.key.appId === appPrice.appId;
    });
  }

  findPackageMatchingAppPrice(storedPackages: Package[], appPrice: AppPrices): Package {
    return storedPackages.find((currentPackage) => {
      return currentPackage.lineItems.lineItems.find((currentLineItem) => {
        return currentLineItem.id === appPrice.appId;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }
}
